import { isToss } from './platform';
import { loadTossSdk, TOSS_EVENTS, tossDeepLink, tossLog } from './toss';

/**
 * 공유 수단 선택 — 모바일은 OS 공유 시트, PC는 클립보드 복사.
 * 토스 미니앱에서는 토스 SDK의 공유를 쓴다 (웹뷰에서는 OS 공유 시트가 열리지 않는다).
 *
 * PC에서 공유 시트를 띄우지 않는 이유: 데스크톱 브라우저도 공유 API를 지원한다고 보고하지만
 * 실제로는 어색한 팝업이 뜨고, 파일의 경우 어디로 갔는지 알기 어렵다.
 */

export const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/** 공유 시트를 쓸 수 있는 상태인지 (카카오톡·메시지 등으로 바로 보내기) */
export const canShareSheet = () => isToss || (isMobile() && typeof navigator.share === 'function');

export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'failed';

/**
 * 링크를 공유한다. 모바일이면 공유 시트, 아니면 클립보드 복사.
 * 사용자가 공유 시트를 닫은 경우(`cancelled`)는 실패가 아니므로 호출부에서 구분해 다룰 것.
 */
export async function shareLink(payload: {
  title: string;
  text: string;
  url: string;
}): Promise<ShareResult> {
  if (isToss) {
    const shared = await shareViaToss(payload);
    if (shared) return 'shared';
    // 토스 공유가 실패하면 복사로 물러난다 (아래 공통 경로)
  } else if (canShareSheet()) {
    try {
      await navigator.share(payload);
      return 'shared';
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled';
      // 공유 시트가 실패하면 복사로 물러난다 — 아무 일도 안 일어나는 것보다 낫다
    }
  }
  try {
    await navigator.clipboard.writeText(`${payload.text}\n${payload.url}`);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/**
 * 토스 미니앱 공유.
 *
 * ⚠️ 넘기는 링크는 **미니앱 딥링크(intoss://)** 여야 한다. 웹 주소를 그대로 보내면
 * 받는 사람이 토스 밖 브라우저에서 열어 방이 갈린다 (초대 동선이 깨지는 지점).
 * SDK가 아직 붙지 않았으면 false를 돌려 호출부가 복사로 물러나게 한다.
 */
async function shareViaToss(payload: { text: string; url: string }): Promise<boolean> {
  const sdk = await loadTossSdk();
  if (!sdk) return false;

  /**
   * 딥링크를 못 만들어도 **공유 자체는 살린다** — 우리 웹 주소로 보낸다.
   *
   * `createLink`는 콘솔 '주요 기능'에 등록되지 않은 경로도 받아준다 (`/r/…`로 실측 확인,
   * 2026-08-28). 그래도 이 폴백을 남겨 두는 이유: 실패하면 공유 버튼이 아무 일도 안 한 것처럼
   * 보이는데, 그것보다는 웹 주소라도 나가는 편이 낫다. 받는 사람은 웹으로 열어도 똑같이 읽는다.
   */
  let link = payload.url;
  try {
    link = await sdk.Share.createLink({ path: tossDeepLink(payload.url) });
  } catch {
    /* 딥링크 실패 — 웹 주소 그대로 보낸다 */
  }

  try {
    await sdk.Share.sendMessage({ message: `${payload.text}\n${link}` });
    tossLog(TOSS_EVENTS.shareComplete);
    return true;
  } catch {
    return false;
  }
}
